import {
  ESessionStorageKeys,
  HomeInformationInput,
  Loader,
  StepperFormFooter,
  useGetCreditApplicationById,
  useGetProvincesConfigs,
  useMerchantFinancingConfig,
} from '@applyonline/shared'
import { scrollToTop } from '@applyonline/shared/utils'
import { EFinancingProgram, ELoanPurpose } from '@if/core/types'
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { Constants } from '../../types'
import { Applicant } from '../../types/Schemas/ApplicantSchema'
import { PersonalInformationInput, Prequalification } from '../../types/Schemas/PrequalificationSchema'
import AddressForm from './components/AddressForm'
import ContactInformationForm from './components/ContactInformationForm'
import EmploymentForm from './components/EmploymentForm'
import PersonalInformationForm from './components/PersonalInformationForm'
import ReviewCoapplicant from './components/ReviewCoapplicant'
import StepProgressionBar from './components/StepProgressionBar'
import {
  buildDefaultCoapplicant,
  getMonthlyIncome,
  useSessionStorage,
  useStepListForApplicant,
} from './submit-app-hooks'

const AddCoapplicantPage = () => {
  const urlParams = useParams()
  const id = urlParams.id!
  const currentStep = urlParams.currentStep!
  const navigate = useNavigate()
  
  const applicationURL = `/creditapplication/${id}`
  const addCoappURL = `${applicationURL}/addcoapplicant`

  useEffect(() => {
    const savedcoApplicantInfo = sessionStorage.getItem(ESessionStorageKeys.COAPPLICANT_INFO)
    if (!savedcoApplicantInfo) navigate(`${addCoappURL}/personal-information`)
  }, [addCoappURL, navigate])

  const { t } = useTranslation()

  const steps = useStepListForApplicant()
  
  const cls = `form-section step-${currentStep}-active`

  const [creditApp, isLoadingCreditApp] = useGetCreditApplicationById(id, EFinancingProgram.Personal, false)

  // Enagic applications never allow a co-applicant (see #60192): redirect back to the application
  // so a direct/bookmarked add-coapplicant link cannot reach the form.
  useEffect(() => {
    if (creditApp && Constants.MerchantsThatForbidHavingCoapplicants.includes(creditApp.merchantId)) {
      navigate(applicationURL, { replace: true })
    }
  }, [creditApp, navigate, applicationURL])

  const [provinceConfigs, isLoadingConfig] = useGetProvincesConfigs(EFinancingProgram.Personal)
  const [merchantFinancingConfig, isFetchingMerchantConfig] = useMerchantFinancingConfig(
    creditApp?.merchantId ?? '',
    EFinancingProgram.Personal,
  )

  const financingConfig = useMemo(() => {
    if (!creditApp || !merchantFinancingConfig) return null

    const isMedicalCategory = [ELoanPurpose.Medical, ELoanPurpose.Aesthetics, ELoanPurpose.Dentistry].includes(
      creditApp.loanPurposeId,
    )
    const defaultMaxLoanAmount = isMedicalCategory ? Constants.MaxLoanAmountMedicard : Constants.MaxLoanAmount

    return {
      ...merchantFinancingConfig,
      maximumLoanAmount: [Constants.MaxLoanAmount, Constants.MaxLoanAmountMedicard].includes(
        merchantFinancingConfig.maximumLoanAmount,
      )
        ? defaultMaxLoanAmount
        : merchantFinancingConfig.maximumLoanAmount,
      minimumLoanAmount: merchantFinancingConfig.minimumLoanAmount ?? Constants.MinLoanAmount,
      maxLoanTerm: merchantFinancingConfig.maxLoanTerm ?? Constants.BaseMaxTerm,
    }
  }, [creditApp, merchantFinancingConfig])

  const [prequalInfo, setPrequalInfo] = useSessionStorage<Prequalification>(
    ESessionStorageKeys.COAPPLICANT_INFO,
    financingConfig ? buildDefaultCoapplicant(financingConfig) : ({} as Prequalification),
  )

  useEffect(() => {
    if (financingConfig && !prequalInfo.applicant) {
      setPrequalInfo(buildDefaultCoapplicant(financingConfig))
    }
  }, [financingConfig, prequalInfo.applicant, setPrequalInfo])

  const [disableNext, setDisableNext] = useState(false)

  const gotoStep = useCallback((stepPath: string) => {
    scrollToTop()
    navigate(`${addCoappURL}/${stepPath}`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const nextStepAfter = useCallback(() => {
    const nextStepIdx = steps.indexOf(currentStep.toLowerCase()) + 1
    if (nextStepIdx < steps.length) {
      gotoStep(steps[nextStepIdx])
    }
  }, [currentStep, gotoStep, steps])

  const handlePrequalificationChanged = ({ applicant, ...data }: PersonalInformationInput) => {
    const update = {
      ...prequalInfo,
      ...data,
      applicant: { ...prequalInfo.applicant, ...applicant },
    } as Prequalification
    setPrequalInfo(update)
  }

  const handleApplicantChanged = (data: Partial<Applicant>) => {
    const update = {
      ...prequalInfo,
      applicant: { ...prequalInfo.applicant, ...data },
    }
    setPrequalInfo(update)
  }

  const handleAddressUpdated = (data: HomeInformationInput) => {
    handleApplicantChanged({
      currentAddress: data.currentAddress,
      spending: data.spending,
    } as Partial<Applicant>)
  }

  useLayoutEffect(scrollToTop, [])

  if (
    isLoadingCreditApp ||
    isLoadingConfig ||
    isFetchingMerchantConfig ||
    !creditApp ||
    !financingConfig ||
    !prequalInfo.applicant
  )
    return <Loader title={t('common.withYouShortly')} description={t('common.pleaseWait')} />
  return (
    <main id="add-coapplicant-form" className={cls}>
      <StepProgressionBar steps={steps} currentStep={currentStep} isCoapplicant />
      {currentStep === 'personal-information' && (
        <PersonalInformationForm
          isCoapplicant
          prequalificationData={prequalInfo}
          onPrequalificationChanged={handlePrequalificationChanged}
          nextStepAfter={nextStepAfter}
          currentStep={currentStep}
          loanPurposeId={prequalInfo.loanPurposeId}
        />
      )}

      {currentStep === 'contact' && (
        <ContactInformationForm
          onApplicantChanged={handleApplicantChanged}
          nextStepAfter={nextStepAfter}
          currentStep={currentStep}
          applicantData={prequalInfo.applicant}
          prohibedPhone={creditApp.applicant.cellPhone}
          loanPurposeId={creditApp.loanPurposeId}
          firstName={prequalInfo.applicant.firstName}
          middleName={prequalInfo.applicant.middleName}
          lastName={prequalInfo.applicant.lastName}
          languageId={prequalInfo.applicant.languageId}
        />
      )}

      {currentStep === 'address' && (
        <AddressForm
          applicantData={prequalInfo.applicant}
          onApplicantChanged={handleAddressUpdated}
          nextStepAfter={nextStepAfter}
          currentStep={currentStep}
          applicantName={prequalInfo.applicant.firstName}
          applicantBirthDate={prequalInfo.applicant.birthDate}
          monthlyIncome={getMonthlyIncome(prequalInfo.applicant)}
          setDisableNext={setDisableNext}
        />
      )}

      {currentStep === 'employment' && (
        <EmploymentForm
          applicantData={prequalInfo.applicant}
          onApplicantChanged={handleApplicantChanged}
          nextStepAfter={nextStepAfter}
          currentStep={currentStep}
        />
      )}

      {currentStep === 'review' && (
        <ReviewCoapplicant
          gotoStep={gotoStep}
          prequalification={prequalInfo}
          creditApplicationId={id}
          provinceConfigs={provinceConfigs}
          financingProgramConfig={financingConfig}
        />
      )}

      {currentStep !== 'review' && (
        <StepperFormFooter
          currentStepIndex={steps.indexOf(currentStep)}
          steps={steps}
          disableBack={false}
          disableNext={disableNext}
          baseURL={steps.indexOf(currentStep) === 0 ? applicationURL : addCoappURL}
        />
      )}
    </main>
  )
}

export default AddCoapplicantPage
